import Tabs from '../../../components/AnimatedTabs';
import Table from "../../../components/RecordTable";
import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,

  Form,
  Input,
  Button,
  Upload,
  Select,
  InputNumber,
  Space,
  Modal,
  message,
  Switch,
  Tag,
  Popconfirm,
  Image,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import theme from "../../../config/theme";
import { generalSettingsAPI, labTestAPI } from "../../../services/api";

const { Content } = Layout;
const { TextArea } = Input;

const HomeSettingsPage = () => {
  const [activeTab, setActiveTab] = useState("banners");
  const [bannerForm] = Form.useForm();
  const [quickActionForm] = Form.useForm();
  const [popularTestForm] = Form.useForm();

  const [bannerModal, setBannerModal] = useState(false);
  const [quickActionModal, setQuickActionModal] = useState(false);
  const [popularTestModal, setPopularTestModal] = useState(false);

  const [editingBanner, setEditingBanner] = useState(null);
  const [editingQuickAction, setEditingQuickAction] = useState(null);
  const [editingTest, setEditingTest] = useState(null);

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allTests, setAllTests] = useState([]);

  const [bannerFileList, setBannerFileList] = useState([]);
  const [quickActionFileList, setQuickActionFileList] = useState([]);

  // Gradient options matching your theme
  const gradientOptions = [
    { label: "Primary Blue", value: "primary" },
    { label: "Secondary Green", value: "secondary" },
    { label: "Purple", value: "purple" },
    { label: "Orange", value: "orange" },
    { label: "Coral", value: "coral" },
  ];

  // Route options for navigation
  const routeOptions = [
    { label: "Book Appointment", value: "BookAppointment" },
    { label: "Book Test", value: "BookTest" },
    { label: "Membership", value: "Membership" },
    { label: "Rewards", value: "Rewards" },
    { label: "Test Details", value: "TestDetails" },
    { label: "Appointments", value: "Appointments" },
    { label: "My Doctors", value: "MyDoctors" },
  ];

  // Icon/Emoji options
  const emojiOptions = [
    { label: "🏥 Hospital", value: "🏥" },
    { label: "💎 Diamond", value: "💎" },
    { label: "👨‍⚕️ Doctor", value: "👨‍⚕️" },
    { label: "🔬 Lab", value: "🔬" },
    { label: "💳 Card", value: "💳" },
    { label: "🎁 Gift", value: "🎁" },
    { label: "📅 Calendar", value: "📅" },
    { label: "⭐ Star", value: "⭐" },
  ];

  useEffect(() => {
    fetchSettings();
    fetchAllTests();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await generalSettingsAPI.getHomePageSettings();
      // Access the correct nested data structure
      setSettings(response.data);
    } catch (error) {
      message.error("Failed to fetch settings");
      console.error("Settings fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTests = async () => {
    try {
      const response = await labTestAPI.getAll();
      // Access the correct nested data structure
      setAllTests(response.data || []);
    } catch (error) {
      message.error("Failed to fetch tests");
      console.error("Tests fetch error:", error);
    }
  };

  // ==================== BANNER MANAGEMENT ====================

  const bannerColumns = [
    {
      title: "Preview",
      dataIndex: "imageUrl",
      key: "preview",
      render: (imageUrl, record) =>
        imageUrl ? (
          <Image
            src={imageUrl}
            alt={record.title}
            width={120}
            height={40}
            style={{ objectFit: "cover", borderRadius: 8 }}
          />
        ) : (
          <div
            style={{
              width: 120,
              height: 40,
              background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 20,
            }}
          >
            {record.emoji}
          </div>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Subtitle",
      dataIndex: "subtitle",
      key: "subtitle",
    },
    {
      title: "Type",
      key: "type",
      render: (record) => (
        <Tag color={record.imageUrl ? "blue" : "green"}>
          {record.imageUrl ? "Image" : "Gradient"}
        </Tag>
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleToggleBannerStatus(record._id, checked)}
        />
      ),
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditBanner(record)}
          />
          <Popconfirm
            title="Delete this banner?"
            onConfirm={() => handleDeleteBanner(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAddBanner = () => {
    setEditingBanner(null);
    bannerForm.resetFields();
    setBannerFileList([]);
    setBannerModal(true);
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    bannerForm.setFieldsValue(banner);

    if (banner.imageUrl) {
      setBannerFileList([
        {
          uid: "-1",
          name: "image.jpg",
          status: "done",
          url: banner.imageUrl,
        },
      ]);
    } else {
      setBannerFileList([]);
    }

    setBannerModal(true);
  };

  const handleSaveBanner = async () => {
    try {
      const values = await bannerForm.validateFields();
      setLoading(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      // Add image if uploaded
      if (bannerFileList.length > 0 && bannerFileList[0].originFileObj) {
        formData.append("image", bannerFileList[0].originFileObj);
      }

      if (editingBanner) {
        await generalSettingsAPI.updateBanner(editingBanner._id, formData);
        message.success("Banner updated successfully");
      } else {
        await generalSettingsAPI.createBanner(formData);
        message.success("Banner created successfully");
      }

      setBannerModal(false);
      fetchSettings();
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
      console.error("Banner save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBanner = async (id) => {
    try {
      setLoading(true);
      await generalSettingsAPI.deleteBanner(id);
      message.success("Banner deleted successfully");
      fetchSettings();
    } catch (error) {
      message.error("Failed to delete banner");
      console.error("Banner delete error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBannerStatus = async (id, isActive) => {
    try {
      await generalSettingsAPI.toggleBannerStatus(id, { isActive });
      message.success(`Banner ${isActive ? "activated" : "deactivated"}`);
      fetchSettings();
    } catch (error) {
      message.error("Failed to update status");
      console.error("Banner toggle error:", error);
    }
  };

  // ==================== QUICK ACTION MANAGEMENT ====================

  const quickActionColumns = [
    {
      title: "Image",
      dataIndex: "imageUrl",
      key: "imageUrl",
      render: (imageUrl) => (
        <Image
          src={imageUrl}
          alt="Quick Action"
          width={60}
          height={60}
          style={{ objectFit: "cover", borderRadius: 8 }}
        />
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title) => <div style={{ whiteSpace: "pre-line" }}>{title}</div>,
    },
    {
      title: "Gradient",
      dataIndex: "gradientKey",
      key: "gradientKey",
      render: (gradientKey) => (
        <Tag color={theme.colors[gradientKey] || theme.colors.primary}>
          {gradientKey}
        </Tag>
      ),
    },
    {
      title: "Route",
      dataIndex: "route",
      key: "route",
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) =>
            handleToggleQuickActionStatus(record._id, checked)
          }
        />
      ),
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditQuickAction(record)}
          />
          <Popconfirm
            title="Delete this quick action?"
            onConfirm={() => handleDeleteQuickAction(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleEditQuickAction = (quickAction) => {
    setEditingQuickAction(quickAction);
    quickActionForm.setFieldsValue(quickAction);

    if (quickAction.imageUrl) {
      setQuickActionFileList([
        {
          uid: "-1",
          name: "image.jpg",
          status: "done",
          url: quickAction.imageUrl,
        },
      ]);
    } else {
      setQuickActionFileList([]);
    }

    setQuickActionModal(true);
  };

  const handleSaveQuickAction = async () => {
    try {
      const values = await quickActionForm.validateFields();

      if (!editingQuickAction && quickActionFileList.length === 0) {
        message.error("Please upload an image");
        return;
      }

      setLoading(true);

      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      if (
        quickActionFileList.length > 0 &&
        quickActionFileList[0].originFileObj
      ) {
        formData.append("image", quickActionFileList[0].originFileObj);
      }

      if (editingQuickAction) {
        await generalSettingsAPI.updateQuickAction(
          editingQuickAction._id,
          formData
        );
        message.success("Quick action updated successfully");
      } else {
        await generalSettingsAPI.createQuickAction(formData);
        message.success("Quick action created successfully");
      }

      setQuickActionModal(false);
      fetchSettings();
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
      console.error("Quick action save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuickAction = async (id) => {
    try {
      setLoading(true);
      await generalSettingsAPI.deleteQuickAction(id);
      message.success("Quick action deleted successfully");
      fetchSettings();
    } catch (error) {
      message.error("Failed to delete quick action");
      console.error("Quick action delete error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleQuickActionStatus = async (id, isActive) => {
    try {
      await generalSettingsAPI.toggleQuickActionStatus(id, { isActive });
      message.success(`Quick action ${isActive ? "activated" : "deactivated"}`);
      fetchSettings();
    } catch (error) {
      message.error("Failed to update status");
      console.error("Quick action toggle error:", error);
    }
  };

  // ==================== POPULAR TEST MANAGEMENT ====================

  const popularTestColumns = [
    {
      title: "Icon",
      dataIndex: "customIcon",
      key: "customIcon",
      render: (icon) => <span style={{ fontSize: 24 }}>{icon}</span>,
    },
    {
      title: "Test Name",
      key: "name",
      render: (record) => record.customName || record.testId?.name || "N/A",
    },
    {
      title: "Category",
      key: "category",
      render: (record) => {
        const categories = record.testId?.category;
        if (!categories) return "N/A";

        // Handle both array and string categories
        const categoryArray = Array.isArray(categories)
          ? categories
          : [categories];

        return categoryArray.map((cat, index) => (
          <Tag key={index} color="blue">
            {cat}
          </Tag>
        ));
      },
    },
    {
      title: "Price Range",
      key: "price",
      render: (record) => {
        const minPrice = record.testId?.minPrice;
        const maxPrice = record.testId?.maxPrice;

        if (!minPrice && !maxPrice) return "N/A";

        if (minPrice === maxPrice) {
          return `₹${minPrice}`;
        }

        return `₹${minPrice} - ₹${maxPrice}`;
      },
    },
    {
      title: "Available Labs",
      key: "labsCount",
      render: (record) => record.testId?.availableLabsCount || 0,
    },
    {
      title: "Home Collection",
      key: "homeCollection",
      render: (record) => (
        <Tag color={record.testId?.homeCollectionAvailable ? "green" : "red"}>
          {record.testId?.homeCollectionAvailable ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleToggleTestStatus(record._id, checked)}
        />
      ),
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      sorter: (a, b) => a.order - b.order,
    },
    {
      title: "Actions",
      key: "actions",
      render: (record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTest(record)}
          />
          <Popconfirm
            title="Delete this test?"
            onConfirm={() => handleDeleteTest(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleEditTest = (test) => {
    setEditingTest(test);
    popularTestForm.setFieldsValue({
      testId: test.testId?._id,
      customName: test.customName,
      customIcon: test.customIcon,
      order: test.order,
      isActive: test.isActive,
    });
    setPopularTestModal(true);
  };

  const handleSavePopularTest = async () => {
    try {
      const values = await popularTestForm.validateFields();
      setLoading(true);

      if (editingTest) {
        await generalSettingsAPI.updatePopularTest(editingTest._id, values);
        message.success("Popular test updated successfully");
      } else {
        await generalSettingsAPI.createPopularTest(values);
        message.success("Popular test added successfully");
      }

      setPopularTestModal(false);
      fetchSettings();
    } catch (error) {
      message.error(error.response?.data?.message || "Operation failed");
      console.error("Popular test save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (id) => {
    try {
      setLoading(true);
      await generalSettingsAPI.deletePopularTest(id);
      message.success("Popular test deleted successfully");
      fetchSettings();
    } catch (error) {
      message.error("Failed to delete test");
      console.error("Popular test delete error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTestStatus = async (id, isActive) => {
    try {
      await generalSettingsAPI.togglePopularTestStatus(id, { isActive });
      message.success(`Test ${isActive ? "activated" : "deactivated"}`);
      fetchSettings();
    } catch (error) {
      message.error("Failed to update status");
      console.error("Popular test toggle error:", error);
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error("Image must be smaller than 5MB!");
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent auto upload
    },
    maxCount: 1,
  };

  return (
    <Layout style={{ minHeight: "100vh", background: theme.colors.background }}>
      <Content style={{ padding: theme.spacing.xl }}>
        <Card
          title="Home Screen Management"
          loading={loading}
          extra={
            <Space>
              {activeTab === "banners" && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddBanner}
                  style={{ background: theme.colors.primary }}
                >
                  Add Banner
                </Button>
              )}
              {activeTab === "quickActions" && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingQuickAction(null);
                    quickActionForm.resetFields();
                    setQuickActionFileList([]);
                    setQuickActionModal(true);
                  }}
                  style={{ background: theme.colors.primary }}
                >
                  Add Quick Action
                </Button>
              )}
              {activeTab === "popularTests" && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingTest(null);
                    popularTestForm.resetFields();
                    setPopularTestModal(true);
                  }}
                  style={{ background: theme.colors.primary }}
                >
                  Add Popular Test
                </Button>
              )}
            </Space>
          }
        >
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Banners" key="banners">
              <Table
                columns={bannerColumns}
                dataSource={settings?.banners || []}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Quick Actions" key="quickActions">
              <Table
                columns={quickActionColumns}
                dataSource={settings?.quickActions || []}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Popular Tests" key="popularTests">
              <Table
                columns={popularTestColumns}
                dataSource={settings?.popularTests || []}
                rowKey="_id"
                pagination={{ pageSize: 10 }}
              />
            </Tabs.TabPane>
          </Tabs>
        </Card>

        {/* Banner Modal */}
        <Modal
          title={editingBanner ? "Edit Banner" : "Add Banner"}
          open={bannerModal}
          onOk={handleSaveBanner}
          onCancel={() => setBannerModal(false)}
          width={700}
          confirmLoading={loading}
          okButtonProps={{ style: { background: theme.colors.primary } }}
        >
          <Form
            form={bannerForm}
            layout="vertical"
            initialValues={{
              isActive: true,
              order: 1,
              buttonText: "Learn More",
            }}
          >
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input placeholder="Full Body Checkup" />
            </Form.Item>

            <Form.Item
              name="subtitle"
              label="Subtitle"
              rules={[{ required: true, message: "Please enter subtitle" }]}
            >
              <Input placeholder="Special Discount 40% OFF" />
            </Form.Item>

            <Form.Item name="buttonText" label="Button Text">
              <Input placeholder="Learn More" />
            </Form.Item>

            <Form.Item label="Banner Image (Recommended: 1200x400px)">
              <Upload
                {...uploadProps}
                listType="picture-card"
                fileList={bannerFileList}
                onChange={({ fileList }) => setBannerFileList(fileList)}
              >
                {bannerFileList.length === 0 && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
              <small style={{ color: theme.colors.textSecondary }}>
                Upload an image or use gradient with emoji below
              </small>
            </Form.Item>

            <Form.Item
              name="emoji"
              label="Emoji/Icon (for gradient banners)"
              tooltip="Used only when no image is uploaded"
            >
              <Select
                options={emojiOptions}
                placeholder="Select emoji"
                allowClear
              />
            </Form.Item>

            <Form.Item
              name="gradientKey"
              label="Gradient Theme (for gradient banners)"
              tooltip="Used only when no image is uploaded"
            >
              <Select options={gradientOptions} placeholder="Select gradient" />
            </Form.Item>

            <Form.Item name="route" label="Navigation Route (Optional)">
              <Select
                options={routeOptions}
                placeholder="Select route"
                allowClear
              />
            </Form.Item>

            <Space style={{ width: "100%" }} size="large">
              <Form.Item name="order" label="Display Order">
                <InputNumber min={1} />
              </Form.Item>

              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
          </Form>
        </Modal>

        {/* Quick Action Modal */}
        <Modal
          title={editingQuickAction ? "Edit Quick Action" : "Add Quick Action"}
          open={quickActionModal}
          onOk={handleSaveQuickAction}
          onCancel={() => setQuickActionModal(false)}
          confirmLoading={loading}
          okButtonProps={{ style: { background: theme.colors.primary } }}
        >
          <Form
            form={quickActionForm}
            layout="vertical"
            initialValues={{ isActive: true, order: 1 }}
          >
            <Form.Item name="title" label="Title" rules={[{ required: true }]}>
              <TextArea
                rows={2}
                placeholder="Book&#10;Appointment"
                maxLength={30}
              />
              <small style={{ color: theme.colors.textSecondary }}>
                Use line breaks for multi-line titles (e.g., "Book{"\n"}
                Appointment")
              </small>
            </Form.Item>

            <Form.Item label="Icon Image (Required)" required>
              <Upload
                {...uploadProps}
                listType="picture-card"
                fileList={quickActionFileList}
                onChange={({ fileList }) => setQuickActionFileList(fileList)}
              >
                {quickActionFileList.length === 0 && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                )}
              </Upload>
              <small style={{ color: theme.colors.textSecondary }}>
                Recommended size: 100x100px
              </small>
            </Form.Item>

            <Form.Item
              name="gradientKey"
              label="Gradient"
              rules={[{ required: true }]}
            >
              <Select options={gradientOptions} placeholder="Select gradient" />
            </Form.Item>

            <Form.Item name="route" label="Route" rules={[{ required: true }]}>
              <Select options={routeOptions} placeholder="Select route" />
            </Form.Item>

            <Space style={{ width: "100%" }}>
              <Form.Item name="order" label="Order">
                <InputNumber min={1} />
              </Form.Item>

              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
          </Form>
        </Modal>

        {/* Popular Test Modal */}
        <Modal
          title={editingTest ? "Edit Popular Test" : "Add Popular Test"}
          open={popularTestModal}
          onOk={handleSavePopularTest}
          onCancel={() => setPopularTestModal(false)}
          width={700}
          confirmLoading={loading}
          okButtonProps={{ style: { background: theme.colors.primary } }}
        >
          <Form
            form={popularTestForm}
            layout="vertical"
            initialValues={{ isActive: true, customIcon: "🔬", order: 1 }}
          >
            <Form.Item
              name="testId"
              label="Select Test"
              rules={[{ required: true, message: "Please select a test" }]}
            >
              <Select
                showSearch
                placeholder="Select a test"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={allTests.map((test) => ({
                  label: `${test.name} - ₹${test.minPrice || 0}${
                    test.maxPrice !== test.minPrice
                      ? ` - ₹${test.maxPrice}`
                      : ""
                  }`,
                  value: test._id,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="customName"
              label="Custom Display Name (Optional)"
              tooltip="Leave empty to use original test name"
            >
              <Input placeholder="Custom name for display" />
            </Form.Item>

            <Form.Item name="customIcon" label="Icon">
              <Select options={emojiOptions} placeholder="Select icon" />
            </Form.Item>

            <Space style={{ width: "100%" }}>
              <Form.Item name="order" label="Order">
                <InputNumber min={1} />
              </Form.Item>

              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Space>
          </Form>
        </Modal>
      </Content>
    </Layout>
  );
};

export default HomeSettingsPage;
